import cn from 'clsx'
import React, { useCallback, useContext, useRef, useEffect } from 'react'
import BedbankRoomOptions from './BedbankRoomOptions'
import BedbankSidebarFilters from './BedbankSidebarFilters'
import Pane from 'components/Common/Pane'
import BedbankOfferIntro from './BedbankOfferIntro'
import Breakpoint from 'components/Common/Breakpoint'
import LayoutContainer from 'components/Common/LayoutContainer'
import { CALENDAR_GA_EVENT_ACTION, GA_ACTION_TO_SNOWPLOW_MAP } from 'constants/analytics'
import { BedbankPriceChangeModalProvider } from 'contexts/bedbankPriceChangeModalContext'
import { OfferPageActions, OfferPageFilterTypes } from 'contexts/OfferPage/offerPageStateReducer'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { useSyncBedbankTripItem } from 'tripPlanner/hooks/bookmarks/useSyncBedbankTripItem'
import BedbankOfferHeader from './BedbankOfferHeader'
import Backdrop from 'components/utils/Backdrop'
import OfferPageGallery from '../OfferPageGallery'
import styled from 'styled-components'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import DesktopRelatedTravelItems from 'components/OfferPage/RelatedTravelItems'
import OfferPageStateContext from 'contexts/OfferPage/offerPageStateContext'
import UserAnalyticsActionContext from 'contexts/userAnalyticsActionContext'
import CalendarDatePickerSelectBox from './BedbankDatePicker/CalendarDatePickerSelectBox'
import CalendarDatePickerSelect from './BedbankDatePicker/CalendarDatePickerSelect'
import OfferDatePickerSelectBox from '../Filters/DatePicker/OfferDatePickerSelectBox'
import OfferDatePickerSelect from '../Filters/DatePicker/OfferDatePickerSelect'
import OfferAirportSelectBox from '../Filters/Airport/OfferAirportSelectBox'
import OfferAirportSelect from '../Filters/Airport/OfferAirportSelect'
import OfferCapacityWithAgesSelectBox from '../Filters/CapacityWithAges/OfferCapacityWithAgesSelectBox'
import OfferCapacityWithAgesSelect from '../Filters/CapacityWithAges/OfferCapacityWithAgesSelect'
import { rem } from 'polished'
import config from 'constants/config'
import Heading from 'components/Luxkit/Typography/Heading'
import { InViewHookResponse } from 'react-intersection-observer'
import { getCapacitiesForBedbankOffer } from 'lib/offer/offerUtils'
import { useAppSelector } from 'hooks/reduxHooks'
import OfferPageStateTrackingContext from 'contexts/OfferPage/offerPageStateTrackingContext'
import Subtitle from 'components/Luxkit/Typography/Subtitle'
import BedbankDurationTabs from './BedbankDurationTabs'
import AnalyticsComponentContext, { AnalyticsComponent } from 'contexts/Analytics/analyticsComponentContext'
import Group from 'components/utils/Group'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import BedbankOfferPageStickyFooter from '../BedbankOfferPageStickyFooter'
import { useScreenSizeUp } from 'hooks/useScreenSize'

const BarShadow = styled(Pane)`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  top: 0;
  pointer-events: none;
`

const RelativeBlock = styled.div`
  position: relative;
`

const PackageOptionsPane = styled(Pane)`
  padding: ${rem(20)} 0;
`

const DesktopFilterBlock = styled(Group)`
  &.open {
    position: relative;
    z-index: 3;
  }
`

interface Props {
  offer: App.BedbankOffer;
  checkinDate: moment.Moment;
  checkoutDate: moment.Moment;
  rooms: Array<App.Occupants>;
  activeFilter: OfferPageFilterTypes;
  flightPrice?: number;
  flightsEnabled: boolean;
  isCalendarEnabled: boolean;
  duration: number;
  canShowYmalFooter: boolean;
  ymalStickyFooterHideTriggerRef: InViewHookResponse['ref'],
  ymalStickyFooterShowTriggerRef: InViewHookResponse['ref'],
}

function BedbankOfferContent({
  activeFilter,
  offer,
  rooms,
  checkinDate,
  checkoutDate,
  flightsEnabled,
  flightPrice,
  duration,
  isCalendarEnabled,
  canShowYmalFooter,
  ymalStickyFooterHideTriggerRef,
  ymalStickyFooterShowTriggerRef,
}: Props) {
  const dispatch = useOfferPageDispatch()
  const state = useContext(OfferPageStateContext)
  const headlessMode = useAppSelector(state => state.config.headlessMode)

  const packageOptionsAnchor = useRef<HTMLDivElement>(null)

  const isTabletScreenSizeAndUp = useScreenSizeUp('tablet')

  const scrollToPackageOptions = useCallback(() => packageOptionsAnchor.current?.scrollIntoView(), [])

  // 'auto' scroll (not from a user action) only occurs on tablet and up sizes
  // as we don't want the users scroll to lose their position on mobile
  const autoScrollToOptions = useCallback(() => {
    if (isTabletScreenSizeAndUp) {
      scrollToPackageOptions()
    }
  }, [isTabletScreenSizeAndUp, scrollToPackageOptions])
  const { trackEvent } = useContext(OfferPageStateTrackingContext)

  const onViewPackageOptions = useCallback(() => {
    scrollToPackageOptions()
    trackEvent('VIEW_PACKAGE_OPTIONS_PRESSED')
  }, [scrollToPackageOptions, trackEvent])

  useEffect(() => {
    if (activeFilter) {
      autoScrollToOptions()
    }
  }, [activeFilter]) // eslint-disable-line react-hooks/exhaustive-deps

  const onUserAnalyticsAction = useCallback((action: string) => {
    const snowplowEvent = GA_ACTION_TO_SNOWPLOW_MAP[action]
    if (snowplowEvent) {
      trackEvent(snowplowEvent)
    }
  }, [trackEvent])

  const closeFilters = useCallback(() => {
    if (activeFilter === 'datepicker') {
      onUserAnalyticsAction(CALENDAR_GA_EVENT_ACTION.CloseFilter)
    }
    dispatch({ type: OfferPageActions.closeFilter })
  }, [onUserAnalyticsAction, activeFilter, dispatch])
  const capacities = getCapacitiesForBedbankOffer(offer)

  useSyncBedbankTripItem(state, offer)

  const desktopFiltersWrapperRef = useRef<HTMLDivElement>(null)

  return (
    <UserAnalyticsActionContext.Provider value={onUserAnalyticsAction}>
      <BedbankPriceChangeModalProvider>
        <RelativeBlock>
          <CSSBreakpoint min="tablet">
            <BedbankOfferHeader
              offer={offer}
              rooms={rooms}
              duration={duration}
              checkinDate={checkinDate}
              checkoutDate={checkoutDate}
              walledGarden={offer.walledGarden}
              onViewPackageOptions={onViewPackageOptions}
              flightPrice={flightPrice}
              flightsEnabled={flightsEnabled}
            >
              {!headlessMode && <DesktopRelatedTravelItems offer={offer} />}
              <Backdrop onClick={closeFilters} visible={!!activeFilter} />
              <BarShadow shadow type="clear" />
            </BedbankOfferHeader>
          </CSSBreakpoint>
          <span ref={ymalStickyFooterHideTriggerRef} />
          <OfferPageGallery offer={offer} />
          <Breakpoint max="mobile">
            <BedbankSidebarFilters
              activeFilter={activeFilter}
              offer={offer}
              onViewPackageOptions={onViewPackageOptions}
            />
          </Breakpoint>
          <Pane>
            <BedbankOfferIntro
              checkinDate={checkinDate}
              checkoutDate={checkoutDate}
              offer={offer}
              duration={duration}
              onViewPackageOptions={onViewPackageOptions}
              rooms={rooms}
              flightPrice={flightPrice}
              flightsEnabled={flightsEnabled}
            />
          </Pane>
        </RelativeBlock>
        <span ref={ymalStickyFooterShowTriggerRef} />
        <PackageOptionsPane ref={packageOptionsAnchor} type="light" id="Package-Options">
          <VerticalSpacer gap={20}>
            <LayoutContainer>
              <VerticalSpacer gap={32}>
                <VerticalSpacer gap={16}>
                  <Heading variant="heading3" format="titlecase" as="h2" align="center">
                    {config.brandConfig.travelSynonym} Options
                  </Heading>
                  <CSSBreakpoint max="mobile">
                    <Group direction="vertical" gap={8}>
                      <OfferCapacityWithAgesSelectBox />
                      {isCalendarEnabled ? <CalendarDatePickerSelectBox /> : <OfferDatePickerSelectBox />}
                      {flightsEnabled && <OfferAirportSelectBox />}
                    </Group>
                  </CSSBreakpoint>
                  <CSSBreakpoint min="tablet">
                    <DesktopFilterBlock ref={desktopFiltersWrapperRef} direction="horizontal" gap={8} horizontalAlign="center" className={cn({ open: !!activeFilter })}>
                      <OfferCapacityWithAgesSelect boundaryRef={desktopFiltersWrapperRef} capacities={capacities} />
                      {isCalendarEnabled ? <CalendarDatePickerSelect offsetDropdown /> : <OfferDatePickerSelect boundaryRef={desktopFiltersWrapperRef} />}
                      {flightsEnabled && <OfferAirportSelect />}
                    </DesktopFilterBlock>
                  </CSSBreakpoint>
                </VerticalSpacer>
                {isCalendarEnabled && (<VerticalSpacer gap={8}>
                  <Subtitle variant="subtitle3" align="center">
                    Select duration
                  </Subtitle>
                  <BedbankDurationTabs />
                </VerticalSpacer>)}
              </VerticalSpacer>
            </LayoutContainer>
            <VerticalSpacer gap={16}>
              <AnalyticsComponentContext.Provider value={AnalyticsComponent.roomOption}>
                <BedbankRoomOptions
                  offer={offer}
                  checkinDate={checkinDate}
                  checkoutDate={checkoutDate}
                  duration={duration}
                  rooms={rooms}
                  activeFilter={activeFilter}
                  cheapestFlightPrice={flightPrice}
                  flightsEnabled={flightsEnabled}
                  onViewPackageOptions={onViewPackageOptions}
                />
                {!canShowYmalFooter &&
                  <BedbankOfferPageStickyFooter
                    offer={offer}
                    rooms={rooms}
                    duration={duration}
                    checkinDate={checkinDate}
                    checkoutDate={checkoutDate}
                    walledGarden={offer.walledGarden}
                    onScrollTo={onViewPackageOptions}
                    flightPrice={flightPrice}
                    flightsEnabled={flightsEnabled}
                  />
}              </AnalyticsComponentContext.Provider>
            </VerticalSpacer>
          </VerticalSpacer>
        </PackageOptionsPane>
      </BedbankPriceChangeModalProvider>
    </UserAnalyticsActionContext.Provider>
  )
}

export default BedbankOfferContent
